import { useMemo } from 'react';
import { useRawCalls } from './useRawCalls';
import { decodeCallResult, encodeCallData } from '../helpers';
import { useChainId } from './useChainId';
/**
 * Makes a call to a specific method of a specific contract and returns the value or an error if present.
 * The hook will cause the component to refresh when a new block is mined and the return value changes.
 * A syntax sugar for useRawCall that uses ABI, function name, and arguments instead of raw data.
 * If typechain contract is used in call parameter then method name and arguments will be type checked.
 * Result will be typed as well.
 *
 * @param call a single call to a contract , also see {@link Call}
 * @returns The hook returns {@link CallResult} type.
 *          That is: undefined when call didn't return yet or a object { value | error } if it did,
 *          value: any[] | undefined - array of results or undefined if error occurred,
 *          error: Error | undefined - encountered error or undefined if call was successful.
 */
export function useCall(call, queryParams = {}) {
    return useCalls([call], queryParams)[0];
}
/**
 * Makes calls to specific methods of specific contracts and returns values or an error if present.
 * The hook will cause the component to refresh when a new block is mined and the return values change.
 * A syntax sugar for useRawCalls that uses ABI, function name, and arguments instead of raw data.
 * @param calls a list of contract calls , also see {@link Call}.
 * @param queryParams see {@link QueryParams}.
 * @returns a list of results (see {@link CallResult} in {@link useCall} above).
 */
export function useCalls(calls, queryParams = {}) {
    const chainId = useChainId({ queryParams });
    const { isStatic } = queryParams;
    const rawCalls = useMemo(() => calls.map((call) => (chainId !== undefined ? encodeCallData(call, chainId, isStatic) : undefined)), [
        JSON.stringify(calls.map((call) => call && { address: call.contract.address.toLowerCase(), method: call.method, args: call.args })),
        chainId,
    ]);
    const results = useRawCalls(rawCalls);
    return useMemo(() => results.map((result, idx) => decodeCallResult(calls[idx], result)), [results]);
}
//# sourceMappingURL=useCall.js.map